import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { regenerateLimit } from '@/api/insights'
import type { DigestAction, DigestPeriod, DigestPoint, DigestReport, DigestVersion } from '@/api/types'
import { Badge, Card, EmptyState, formatRelativeTime, Icon, LoadingBlock, SegmentedControl, useNow, type IconName } from '@/design'
import { usePermissions } from '@/lib/access'
import { ApiError } from '@/lib/api'
import { usePageFreshness } from '@/lib/freshness'

import { FactsDrawer } from '@/components/insights/FactsDrawer'
import { formatFactPeriod } from '@/lib/insights/factFormat'
import { isAiWritten, writerLabel } from '@/lib/insights/provenance'
import { useDigestQuery, useRegenerateDigest } from '@/lib/insights/queries'

import { useDrillDown } from './hooks'
import './insights.css'

const PERIODS: ReadonlyArray<{ value: DigestPeriod; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
]

const LATEST = 'latest'

function RecommendedActions({ actions }: { actions: DigestAction[] }) {
  const href = useDrillDown()
  if (actions.length === 0) return <p className="in-muted">No actions suggested for this period.</p>
  return (
    <div className="in-action-row">
      {actions.map((action, index) => (
        <Link
          key={`${action.label}-${index}`}
          className="ci-button"
          data-variant={index === 0 ? 'primary' : undefined}
          to={href(action.drillDown)}
        >
          {action.label}
        </Link>
      ))}
    </div>
  )
}

function PointList({ title, icon, points, empty }: { title: string; icon: IconName; points: DigestPoint[]; empty: string }) {
  const href = useDrillDown()
  return (
    <Card
      className="in-section-card"
      title={
        <span className="in-title-with-icon">
          <Icon name={icon} size={16} />
          {title}
        </span>
      }
    >
      {points.length === 0 ? (
        <p className="in-muted">{empty}</p>
      ) : (
        <ul className="in-points">
          {points.map((point, index) =>
            point.drillDown ? (
              <li key={index}>
                <Link to={href(point.drillDown)}>{point.text}</Link>
              </li>
            ) : (
              <li key={index}>{point.text}</li>
            ),
          )}
        </ul>
      )}
    </Card>
  )
}

/** "Version 3 · 2 hours ago · Summary of your figures". */
function versionLabel(version: DigestVersion, now: Date): string {
  return `Version ${version.version} · ${formatRelativeTime(version.generatedAt, now)} · ${writerLabel(version.writer)}`
}

/** The version picker: the latest unless the reader chose an earlier one. */
function VersionPicker({
  report,
  value,
  onChange,
}: {
  report: DigestReport
  value: number | undefined
  onChange: (version: number | undefined) => void
}) {
  const now = useNow()
  if (report.versions.length < 2) return null
  const latest = Math.max(...report.versions.map((version) => version.version))
  return (
    <label className="ci-field ci-field--inline">
      <span>Version</span>
      <select
        value={value === undefined ? LATEST : String(value)}
        onChange={(event) => onChange(event.target.value === LATEST ? undefined : Number(event.target.value))}
      >
        {report.versions.map((version) => (
          <option key={version.version} value={version.version === latest ? LATEST : String(version.version)}>
            {versionLabel(version, now)}
            {version.version === latest ? ' (latest)' : ''}
          </option>
        ))}
      </select>
    </label>
  )
}

/** "about 5 hours" until `deadline`: a wait, written out, never a business figure. */
function waitLabel(milliseconds: number): string {
  const seconds = milliseconds / 1000
  if (seconds < 3600) {
    const minutes = Math.max(1, Math.ceil(seconds / 60))
    return `about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  }
  const hours = Math.ceil(seconds / 3600)
  return `about ${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

/**
 * Writes a new version now, for a role holding `insights.manage`. The API's
 * limit counts every regeneration of the day, daily and weekly together, so
 * once it answers 429 the button stays withdrawn whichever period is shown,
 * until the moment it named (midnight UTC) — counted down, not frozen. A 429
 * that names no moment says the limit is reached but leaves the button
 * offered: nothing would ever lift a withdrawal it cannot time.
 */
function RegenerateButton({ period, onDone }: { period: DigestPeriod; onDone: () => void }) {
  const regenerate = useRegenerateDigest()
  const now = useNow(30_000)
  const refused = regenerateLimit(regenerate.error)
  const until =
    refused?.retryAfterSeconds === null || refused?.retryAfterSeconds === undefined
      ? null
      : regenerate.submittedAt + refused.retryAfterSeconds * 1000
  // The ticking clock can lag the request it is compared with: never read it as earlier than that.
  const current = Math.max(now.getTime(), regenerate.submittedAt)
  const limited = until !== null && current < until
  const limitUntimed = refused !== null && until === null
  const failed = regenerate.variables === period && regenerate.isError && refused === null

  return (
    <div className="in-regenerate">
      <button
        type="button"
        className="ci-button"
        disabled={regenerate.isPending || limited}
        onClick={() => regenerate.mutate(period, { onSuccess: onDone })}
      >
        {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
      </button>
      {limited || limitUntimed ? (
        <p className="in-muted" role="status">
          {refused?.limit === null || refused?.limit === undefined
            ? 'Digests have been regenerated as often as they can be today.'
            : `Digests can be regenerated ${refused.limit} times a day, daily and weekly together, and today's are used.`}
          {until === null ? '' : ` You can regenerate again in ${waitLabel(until - current)}.`}
        </p>
      ) : null}
      {failed ? (
        <p className="in-alert__error" role="alert">
          The digest could not be regenerated. Try again.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Insights > Digest: a management briefing, not a chatbot. The period's
 * executive summary, what improved, what needs attention, opportunities and a
 * row of recommended actions, as the API wrote them — every figure rendered by
 * the API from your own data. Labelled by who wrote it (ADR-0009 D9.7): "Written
 * by AI from your figures" only for a model's text, "Summary of your figures"
 * for the template's. The facts drawer lists every fact the digest cites; an
 * estimate (D9.11) appears only here, badged, with its range and assumption.
 */
export function AiDigestPage() {
  const [period, setPeriod] = useState<DigestPeriod>('daily')
  // A version is a version of one period's digest: remembered with that period's start.
  const [pinned, setPinned] = useState<{ version: number; periodStart: string } | undefined>(undefined)
  const version = pinned?.version
  const digest = useDigestQuery({ period, version })
  const canManage = usePermissions().can('insights.manage')
  const now = useNow()
  usePageFreshness(digest.data?.meta)
  const report = digest.data?.data
  const written = report?.digest ?? null
  const ai = isAiWritten(report?.writer ?? null)

  // A pinned version answers for the latest period that has a digest, so once a newer period is
  // written it is another day's version — or none (a 404). Either way, go back to the latest.
  const pinnedGone =
    pinned !== undefined &&
    ((digest.error instanceof ApiError && digest.error.status === 404) ||
      (report !== undefined && report.version === pinned.version && report.periodStart !== pinned.periodStart))
  useEffect(() => {
    if (pinnedGone) setPinned(undefined)
  }, [pinnedGone])

  const choosePeriod = (next: DigestPeriod) => {
    setPeriod(next)
    setPinned(undefined)
  }
  const chooseVersion = (next: number | undefined) =>
    setPinned(next === undefined || !report ? undefined : { version: next, periodStart: report.periodStart })

  return (
    <div className="in-stack">
      <div className="in-period-row">
        <SegmentedControl label="Digest period" options={PERIODS} value={period} onChange={choosePeriod} />
        {report ? <VersionPicker report={report} value={version} onChange={chooseVersion} /> : null}
        {canManage ? <RegenerateButton period={period} onDone={() => setPinned(undefined)} /> : null}
      </div>

      <Card
        className="in-summary"
        title={
          <span className="in-title-with-icon">
            {ai ? <Icon name="sparkle" size={16} /> : null}
            Executive summary
          </span>
        }
        subtitle={
          report ? (
            <span className="in-provenance">
              {report.writer ? <Badge tone={ai ? 'accent' : 'neutral'}>{writerLabel(report.writer)}</Badge> : null}
              <span>{formatFactPeriod(report.periodStart, report.periodEnd)}</span>
              {report.generatedAt ? <span>· {formatRelativeTime(report.generatedAt, now)}</span> : null}
            </span>
          ) : undefined
        }
        busy={digest.isFetching}
      >
        {digest.isPending ? <LoadingBlock rows={4} label="Loading the digest" /> : null}
        {digest.isError ? <EmptyState variant="outage" compact description="The digest could not be loaded." /> : null}
        {report && !written ? (
          <EmptyState
            variant="no-results"
            compact
            title="No digest yet"
            description={`No ${report.period} digest has been written yet. It appears here once the scheduled run has written it${canManage ? ', or when you regenerate it' : ''}.`}
          />
        ) : null}
        {written ? (
          <>
            <p className="in-summary__headline">{written.headline}</p>
            <p className="in-summary__paragraph" data-testid="digest-summary">
              {written.paragraph}
            </p>
            <FactsDrawer facts={report?.facts ?? []} />
          </>
        ) : null}
      </Card>

      {written ? (
        <>
          <div className="in-section-grid">
            <PointList title="What improved" icon="arrowUp" points={written.whatImproved ?? []} empty="Nothing moved enough to call out this period." />
            <PointList title="What needs attention" icon="alert" points={written.whatNeedsAttention ?? []} empty="Nothing needs attention this period." />
            <PointList title="Opportunities" icon="arrowRight" points={written.opportunities ?? []} empty="No standing opportunities right now." />
          </div>

          <Card title="Recommended actions" subtitle="Each opens the page with the detail behind it — nothing here runs on its own.">
            <RecommendedActions actions={written.recommendedActions ?? []} />
          </Card>
        </>
      ) : null}
    </div>
  )
}
